<template>
    <div class="record-notes loading-content-container">
        <div v-if="isLoading" class="loading-overlay d-flex justify-content-center pt-5">
            <csp-loading-indicator/>
        </div>
        <b-row>
            <b-col cols="12">
                <csp-table
                    ref="noteTable"
                    :default-page-options="defaultPageOptions"
                    :default-per-page="defaultPerPage"
                    :disable-filters="disableFilters"
                    :disable-pagination="disablePagination"
                    icon="inticon-comment"
                    item-detail-id-label="."
                    item-detail-url="."
                    :items-ajax-url="urlForTableData"
                    :legacy-data-structure="true"
                    :rows-selectable="false"
                    table-headline="Kommunikation">
                    <template v-slot:above-filter>
                        <b-button
                            v-if="permissions?.canCreateCallback"
                            class="float-right ml-1 mb-2"
                            variant="primary"
                            @click="createCallbackNote">
                            Rückrufbitte
                        </b-button>
                        <csp-record-detail-notes-create
                            v-if="createModalAvailable"
                            class="float-right"
                            :create-child-for="createChildForNoteId"
                            :form-context="creationFormContext"
                            :hide-for-group-options="hideForGroupOptions"
                            :record-id="recordId"
                            @created="$refs.noteTable.loadRecords()"
                            @reset="resetNoteCreation"/>
                        <csp-responsible-insurance-modal
                            class="float-right mr-1 mb-2"
                            :record-id="recordId"
                            @trigger-notes-reload="$refs.noteTable.loadRecords()"/>
                        <div class="clear-float"/>
                    </template>
                    <template v-slot:cell(actions)="item">
                        <div class="table-actions">
                            <a v-if="item.data.canBeCompleted" href="#" title="Als erledigt markieren"
                                @click.prevent="markComplete(item.data.damageRecordNoteId)">
                                <i class="inticon-autoclaim_done"/>
                            </a>
                            <a v-if="item.data.canCreateChildNote" href="#" title="Auf diesen Vermerk antworten"
                                @click.prevent="createChildNote(item.data.damageRecordNoteId)">
                                <i class="inticon-autoclaim_plus"/>
                            </a>
                            <a v-if="item.data.canBeDeleted" href="#" title="Löschen"
                                @click.prevent="deleteNote(item.data.damageRecordNoteId)">
                                <i class="inticon-autoclaim_delete"/>
                            </a>
                            <a v-if="item.data.canIndexNoteFiles" href="#" title="Verknüpfte Dateien anzeigen"
                                @click.prevent="showFilesModal = item.data.damageRecordNoteId">
                                <i class="inticon-autoclaim_atachements">
                                    <span v-if="item.data.files > 0" class="dot"/>
                                </i>

                            </a>
                            <csp-record-detail-notes-document-list
                                v-if="showFilesModal === item.data.damageRecordNoteId"
                                :hide-for-group-options="hideForGroupOptions"
                                :note-id="parseInt(item.data.damageRecordNoteId)"
                                :record-id="recordId"
                                @reset="showFilesModal = null"
                                @upload-success="$refs.noteTable.loadRecords()"/>

                            <a v-if="item.data.canIndexRecipients" href="#" title="Empfänger anzeigen"
                                @click.prevent="showRecipientsModal = item.data.damageRecordNoteId">
                                <i class="inticon-autoclaim_sent"/>
                            </a>
                            <csp-record-detail-notes-recipient-list
                                v-if="showRecipientsModal === item.data.damageRecordNoteId"
                                :note-id="parseInt(item.data.damageRecordNoteId)"
                                :record-id="recordId"
                                @reset="showRecipientsModal = null"/>
                            <a v-if="item.data.canUnsetPriority" href="#" title="Priorisierung aufheben"
                                @click.prevent="removePriority(item.data.damageRecordNoteId)">
                                <i class="inticon-autoclaim_prio"/>
                            </a>
                        </div>
                    </template>
                    <template v-slot:below-body>
                        <slot name="below-body"/>
                    </template>
                </csp-table>
            </b-col>
        </b-row>
    </div>
</template>

<script>
    import Container from '@services/Container';
    import FormDataMixin from '@desktop/components/mixins/FormDataMixin';
    import NoteCreateModal from '@desktop/components/record/notes/NoteCreateModal';
    import DocumentListModal from '@desktop/components/record/notes/DocumentListModal';
    import RecipientListModal from '@desktop/components/record/notes/RecipientListModal';
    import FormContext from '@desktop/components/FormContext';
    import '@desktop/scss/components/record/notes.scss';
    import ResponsibleInsuranceModal from '@desktop/components/record/notes/ResponsibleInsuranceModal';

    const container = Container.getInstance();
    const flashMessenger = container.getFlashMessenger();
    const recordRepositoryShared = container.getRecordRepository();
    const recordRepository = container.getDesktopRecordRepository();

    export default {
        name: 'RecordNotes',
        components: {
            'csp-record-detail-notes-create': NoteCreateModal,
            'csp-record-detail-notes-document-list': DocumentListModal,
            'csp-record-detail-notes-recipient-list': RecipientListModal,
            'csp-responsible-insurance-modal': ResponsibleInsuranceModal,
        },
        mixins: [FormDataMixin],
        props: {
            defaultPerPage: {
                type: Number,
                default: 20,
            },
            defaultPageOptions: {
                type: Array,
                default: () => [20, 30, 50, 200],
            },
            disableFilters: {
                type: Boolean,
                default: false,
            },
            disablePagination: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            const recordId = parseInt(this.$route.params.recordId);
            const damageRecordNoteId = parseInt(this.$route.query.damageRecordNoteId) || 0;
            return {
                recordId,
                damageRecordNoteId,
                isLoading: false,
                showFilesModal: null,
                showRecipientsModal: null,
                hideForGroupOptions: null,
                createChildForNoteId: 0,
                createModalAvailable: true,
                permissions: null,
                creationFormContext: new FormContext({
                    name: 'note-create',
                    objectId: this.recordId,
                    reloadFields: [],
                    handleEditModeByQuery: false,
                    isInEditMode: true,
                }),
            }
        },
        computed: {
            urlForTableData() {
                const damageRecordNoteIdQuery = this.damageRecordNoteId > 0 ?
                    '&damageRecordNoteId=' + this.damageRecordNoteId.toString() :
                    '';
                return '/damage/record/notestabledata?vehicleDamageRecordId=' + this.recordId + damageRecordNoteIdQuery;
            },
        },
        created() {
            this.loadHideForGroupOptions();
            recordRepository.getNoteIndex(this.recordId)
                .then((response) => {
                    this.permissions = response.data.meta.permissions;
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        methods: {
            createCallbackNote() {
                this.isLoading = true;
                recordRepository.createCallbackNote(this.recordId).then(() => {
                    flashMessenger.addSuccess('Die Rückrufbitte wurde erstellt.');
                    this.$refs.noteTable.loadRecords();
                }).finally(() => {
                    this.isLoading = false;
                });
            },
            createChildNote(noteId) {
                this.createChildForNoteId = parseInt(noteId);
            },
            deleteNote(noteId) {
                if (!confirm('Möchten Sie diesen Vermerk wirklich löschen?')) {
                    return;
                }
                this.isLoading = true;
                recordRepository.deleteNote(this.recordId, noteId).then(() => {
                    this.$refs.noteTable.loadRecords();
                    flashMessenger.addSuccess('Der Vermerk wurde gelöscht.');
                }).finally(() => {
                    this.isLoading = false;
                });
            },
            loadHideForGroupOptions() {
                recordRepositoryShared.hideForGroupOptions(this.recordId).then(
                    (options) => {
                        this.hideForGroupOptions = options;
                    }
                ).finally(() => {
                });
            },
            markComplete(noteId) {
                this.isLoading = true;
                recordRepository.markNoteComplete(this.recordId, noteId).then(() => {
                    this.$refs.noteTable.loadRecords();
                    flashMessenger.addSuccess('Der Vermerk wurde als erledigt markiert.');
                }).finally(() => {
                    this.isLoading = false;
                });
            },
            removePriority(noteId) {
                if (!confirm('Möchten Sie die Priorisierung des Vermerks wirklich aufheben?')) {
                    return;
                }

                this.isLoading = true;
                recordRepository.removeNotePriority(this.recordId, noteId).then(() => {
                    this.$refs.noteTable.loadRecords();
                    flashMessenger.addSuccess('Die Priorisierung des Vermerks wurde aufgehoben.');
                }).finally(() => {
                    this.isLoading = false;
                });
            },
            resetNoteCreation() {
                this.createChildForNoteId = 0;
                this.createModalAvailable = false;
                this.$nextTick(() => {
                    this.createModalAvailable = true;
                });
                this.creationFormContext = new FormContext({
                    name: 'note-create',
                    objectId: this.recordId,
                    reloadFields: [],
                    handleEditModeByQuery: false,
                    isInEditMode: true,
                });
            },
        },
    }
</script>
